import { useId, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import type { ZodError } from 'zod';

import { Field, Input, Textarea } from '@/components/ui';

import { useFieldContext } from './contexts';

type CommonProps = {
  label?: string;
  helperText?: string;
  required?: boolean;
  /**
   * MUI compatibility: when true, renders a textarea instead of an input.
   * `rows` sets the visible rows; falls back to `min-height` from CSS otherwise.
   */
  multiline?: boolean;
  rows?: number;
  /**
   * Tenant-curated suggestions (e.g. from a contact.* counter table). Rendered as a
   * native datalist so the input becomes a searchable combobox; free text stays allowed.
   */
  suggestions?: string[];
  /**
   * Accepted but ignored — legacy MUI prop. Replace with `className` during the Phase 4 restyle.
   */
  sx?: unknown;
};

type Props =
  & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur'>
  & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'onBlur' | 'rows'>
  & CommonProps;

const firstError = (errors: ZodError[]) => errors.find((e) => e !== undefined)?.message;

export function TextField({
  label,
  helperText,
  required,
  multiline,
  rows,
  suggestions,
  sx: _sx,
  id,
  ...props
}: Props) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const listId = `${reactId}-list`;
  const field = useFieldContext<string>();
  const touched = field.state.meta.isTouched;
  const errMsg = touched ? firstError(field.state.meta.errors) : undefined;
  const hasSuggestions = !!suggestions?.length;

  return (
    <Field label={label} required={required} error={errMsg} hint={!errMsg ? helperText : undefined} htmlFor={inputId}>
      {multiline ? (
        <Textarea
          id={inputId}
          value={field.state.value ?? ''}
          rows={rows}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          error={!!errMsg}
          required={required}
          {...(props as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <>
          <Input
            id={inputId}
            value={field.state.value ?? ''}
            list={hasSuggestions ? listId : undefined}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            error={!!errMsg}
            required={required}
            {...(props as InputHTMLAttributes<HTMLInputElement>)}
          />
          {hasSuggestions && (
            <datalist id={listId}>
              {(suggestions ?? []).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
        </>
      )}
    </Field>
  );
}
